import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "upper", label: "1 uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "1 lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "1 digit", test: (p) => /\d/.test(p) },
  {
    key: "special",
    label: "1 special character",
    test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p),
  },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const ruleResults = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(pw) })), [pw]);
  const allMet = ruleResults.every((r) => r.ok);
  const matches = pw.length > 0 && pw === pw2;
  const canSubmit = allMet && matches && !!token && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: pw,
      });
      toast.success("Password reset. Please sign in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-cream p-6">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md text-center">
          <XCircle className="w-7 h-7 text-red-600 mx-auto mb-2" />
          <h1 className="font-serif text-2xl text-zinc-900 mb-2">Invalid reset link</h1>
          <p className="text-sm text-zinc-600 mb-4">
            The reset link is missing the security token. Please request a new one.
          </p>
          <Link to="/forgot-password">
            <Button variant="outline">Request new reset link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex bg-cream relative overflow-hidden">
      <img
        src="/brand/login-hero.jpg"
        alt=""
        aria-hidden="true"
        className="lg:hidden absolute inset-0 w-full h-full object-cover object-center z-0"
      />
      <div
        className="lg:hidden absolute inset-0 z-0 pointer-events-none bg-gradient-to-br from-brand-dark/35 via-brand/15 to-brand-dark/55"
        aria-hidden="true"
      />
      <div className="relative hidden lg:block lg:flex-1 overflow-hidden">
        <img
          src="/brand/login-hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-brand-dark/10 pointer-events-none" />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-10 lg:px-6">
        <div
          className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 lg:bg-transparent lg:backdrop-blur-none lg:shadow-none lg:rounded-none lg:p-0"
          data-testid="reset-password-card"
        >
          <div className="flex justify-center mb-6">
            <img
              src="/brand/logo.png"
              alt="Constantiaberg Haematology"
              className="object-contain w-auto"
              style={{ maxWidth: "200px", maxHeight: "72px" }}
            />
          </div>

          <div className="text-center mb-7">
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-zinc-900">
              Set a new password
            </h1>
            <p className="text-sm text-neutralsoft mt-1 font-sans">
              Pick something strong — you'll use it next time you sign in.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form"noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw" className="text-xs font-medium text-zinc-700">
                New password
              </Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="rp-pw"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoComplete="new-password"
                  className="pl-10 h-11"
                  data-testid="reset-pw-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rp-pw2" className="text-xs font-medium text-zinc-700">
                Confirm new password
              </Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="rp-pw2"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  autoComplete="new-password"
                  className="pl-10 h-11"
                  data-testid="reset-pw2-input"
                  required
                />
              </div>
              {pw2.length > 0 && !matches && (
                <p className="text-xs text-red-600" data-testid="reset-mismatch">
                  Passwords don't match.
                </p>
              )}
            </div>

            {/* Strength rules */}
            <ul className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-1.5 text-xs">
              {ruleResults.map((r) => (
                <li
                  key={r.key}
                  className={`flex items-center gap-2 ${r.ok ? "text-emerald-700" : "text-zinc-500"}`}
                  data-testid={`rule-${r.key}`}
                  data-met={r.ok ? "true" : "false"}
                >
                  {r.ok ? (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-zinc-300" />
                  )}
                  <span>{r.label}</span>
                </li>
              ))}
            </ul>

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-11 bg-brand hover:bg-brand-dark text-white disabled:bg-zinc-300"
              data-testid="reset-submit-btn"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset password
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-xs text-brand hover:text-brand-dark font-medium inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
